import Icon from '@components/v2/ui/Icon/Icon';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack, alpha } from '@mui/material';
import { File, Image, Pencil, X } from 'lucide-react';
import LangEnum from '../../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../translation/TranslationContext';
import type { FileCardProps } from '../../fileCard.types';

const FILE_CARD_HEADER_DEFAULT_OPACITY = 0.85;

export const FILE_CARD_MEDIA_ICON_CLASS = 'file-card-media-icon';

interface FileCardHeaderProps
  extends Pick<
    FileCardProps,
    'onRemove' | 'onEdit' | 'fileName' | 'isFileNameHidden' | 'isMediaIconHidden'
  > {
  isImageOrVideo: boolean;
  opacity?: number;
}

const FileCardHeader = ({
  fileName,
  isImageOrVideo,
  onRemove,
  onEdit,
  isFileNameHidden = false,
  isMediaIconHidden = false,
  opacity = FILE_CARD_HEADER_DEFAULT_OPACITY,
}: FileCardHeaderProps) => {
  const { translate } = useTranslation();

  return (
    <Stack
      direction="row"
      sx={(theme) => ({
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1,
        gap: 0.75,
        minHeight: 28,
        position: 'absolute',
        alignItems: 'center',
        paddingInline: 0.75,
        backgroundColor: alpha(theme.palette.background.paper, opacity),
      })}
    >
      {!isMediaIconHidden && (
        <Icon
          size="md"
          className={FILE_CARD_MEDIA_ICON_CLASS}
          icon={isImageOrVideo ? Image : File}
        />
      )}

      {isFileNameHidden ? (
        <Box sx={{ flex: 1 }} />
      ) : (
        <Typography noWrap title={fileName} variant="body2" sx={{ flex: 1, color: 'text.primary' }}>
          {fileName}
        </Typography>
      )}

      {onEdit && (
        <IconButton
          icon={Pencil}
          size="small"
          color="primary"
          onClick={onEdit}
          ariaLabel={translate(LangEnum.GLOBAL_EDIT)}
        />
      )}

      {onRemove && (
        <IconButton
          icon={X}
          size="small"
          color="primary"
          onClick={onRemove}
          ariaLabel={translate(LangEnum.GLOBAL_REMOVE_FILE)}
        />
      )}
    </Stack>
  );
};

export default FileCardHeader;
